
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile, mediaUrl } from "../../Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../Context/AuthContext";

interface FormValues {
    branch_id: string;
    driver_id: string;
    license_number: string;
    start_date: string;
    expiry_date: string;
    remarks?: string;
    image_front?: FileList;
    image_back?: FileList;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function EditLicenseRenewal() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [branches, setBranches] = useState<any[]>([]);
    const [drivers, setDrivers] = useState<any[]>([]);

    const [existingFront, setExistingFront] = useState<string | null>(null);
    const [existingBack, setExistingBack] = useState<string | null>(null);
    const [submissionBlocked, setSubmissionBlocked] = useState(false);
    const [pendingMessage, setPendingMessage] = useState("");
    const { hasPermission } = useAuth();
    const {
        control,
        handleSubmit,
        watch,
        setValue, reset,
        formState: { errors },
    } = useForm<FormValues>();

    const selectedBranch = watch("branch_id");
    const selectedDriver = watch("driver_id");
    const watchedStart = watch("start_date");
    const watchedExpiry = watch("expiry_date");

    // Pending check
    useEffect(() => {
        if (!selectedDriver || !location.state?.id) { setSubmissionBlocked(false); setPendingMessage(""); return; }
        const checkPending = async () => {
            try {
                const response: any = await apiPost("license_renewal/check_pending", { driver_id: selectedDriver, exclude_id: location.state.id });
                if (response?.exists) { setPendingMessage(response.message); setSubmissionBlocked(true); }
                else { setSubmissionBlocked(false); setPendingMessage(""); }
            } catch { setSubmissionBlocked(false); setPendingMessage(""); }
        };
        checkPending();
    }, [selectedDriver, location.state?.id]);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const branchRes: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchRes);
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, []);

    useEffect(() => {
        const fetchDrivers = async () => {
            if (!selectedBranch) {
                setDrivers([]);
                return;
            }
            try {
                const response: any = await apiPost("drivers/list", { branch_id: selectedBranch });
                setDrivers(Array.isArray(response) ? response : (response.data || []));
            } catch (error) {
                console.error(error);
                setDrivers([]);
            }
        };
        fetchDrivers();
    }, [selectedBranch]);

    useEffect(() => {
        const fetchData = async () => {
            if (!location.state || !location.state.id) {
                toast.error("No Renewal ID found");
                navigate(`${process.env.PUBLIC_URL}/license_renewal`);
                return;
            }

            try {
                const result = await apiPost("license_renewal/viewbyid", { id: location.state.id });

                setValue("branch_id", result.branch_id);
                // Trigger driver fetch by setting branch, but wait a tick or use result directly
                const driversData = await apiPost("drivers/list", { branch_id: result.branch_id });
                setDrivers(Array.isArray(driversData) ? driversData : (driversData.data || []));

                setValue("driver_id", result.driver_id);
                setValue("license_number", result.license_number);
                setValue("start_date", result.start_date);
                setValue("expiry_date", result.expiry_date);
                setValue("remarks", result.remarks);
                setExistingFront(result.image_front);
                setExistingBack(result.image_back);

                setLoading(false);
            } catch (error) {
                console.error(error);
                toast.error("Failed to fetch details");
                setLoading(false);
            }
        };
        fetchData();
    }, [location.state, navigate, setValue]);

    const handleDriverChange = (driverId: string) => {
        setValue("driver_id", driverId);
        const driver = drivers.find((d: any) => d.id.toString() === driverId.toString());
        if (driver) {
            setValue("license_number", driver.license_number || "");
        } else {
            setValue("license_number", "");
        }
    };

    const onSubmit = async (data: FormValues) => {
        if (submissionBlocked) {
            toast.error(pendingMessage || "A pending license renewal request already exists for this driver.");
            return;
        }

        setLoading(true);
        try {
            let imageFrontName = undefined;
            if (data.image_front && data.image_front.length > 0) {
                imageFrontName = await uploadFile(data.image_front[0]);
            }

            let imageBackName = undefined;
            if (data.image_back && data.image_back.length > 0) {
                imageBackName = await uploadFile(data.image_back[0]);
            }

            const payload: any = {
                id: location.state.id,
                ...data,
            };

            if (imageFrontName) payload.image_front = imageFrontName;
            else delete payload.image_front; // Don't overwrite if not changed

            if (imageBackName) payload.image_back = imageBackName;
            else delete payload.image_back; // Don't overwrite if not changed

            // Auto-determine status: Completed if all mandatory fields are present
            const hasFront = imageFrontName || !!existingFront;
            const hasBack = imageBackName || !!existingBack;
            payload.status = (
                data.branch_id &&
                data.driver_id &&
                data.license_number?.trim() &&
                data.start_date &&
                data.expiry_date &&
                hasFront &&
                hasBack
            ) ? 'Completed' : 'Pending';

            await apiPost("license_renewal/update", payload);
            toast.success("License Renewal updated successfully", { autoClose: 1000 });
            setTimeout(() => {
                reset();
                navigate(`${process.env.PUBLIC_URL}/license_renewal`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error updating renewal");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Edit License Renewal" parent="Renewal" title="Edit License Renewal" />
            {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
                <Container fluid>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <H5>Renewal Details</H5>
                                <hr />
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Label>Branch <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="branch_id"
                                            control={control}
                                            rules={{ required: "Branch is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.branch_id}>
                                                        <option value="">Select Branch</option>
                                                        {branches.map((b: any) => (
                                                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.branch_id?.message as string}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Driver Name <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="driver_id"
                                            control={control}
                                            rules={{ required: "Driver is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input
                                                        type="select"
                                                        value={field.value}
                                                        onChange={(e) => handleDriverChange(e.target.value)}
                                                        invalid={!!errors.driver_id}
                                                    >
                                                        <option value="">Select Driver</option>
                                                        {drivers.map((d: any) => (
                                                            <option key={d.id} value={d.id}>{d.name}</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.driver_id?.message as string}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>License Number <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="license_number"
                                            control={control}
                                            rules={{ required: "License Number is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} readOnly placeholder="Auto-filled" invalid={!!errors.license_number} />
                                                    <FormFeedback>{errors.license_number?.message as string}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Label>Start Date <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="start_date"
                                            control={control}
                                            rules={{ required: "Start Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.start_date ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => {
                                                            const d = date ? formatDate(date) : "";
                                                            if (watchedExpiry && d && new Date(d) > new Date(watchedExpiry)) {
                                                                toast.error("Start date cannot be after Expiry date");
                                                                field.onChange("");
                                                                return;
                                                            }
                                                            field.onChange(d);
                                                        }}
                                                        placeholderText="YYYY-MM-DD"
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                    />
                                                    <FormFeedback className="d-block">{errors.start_date?.message as string}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label>Expiry Date <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="expiry_date"
                                            control={control}
                                            rules={{ required: "Expiry Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.expiry_date ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => {
                                                            const d = date ? formatDate(date) : "";
                                                            if (watchedStart && d && new Date(d) < new Date(watchedStart)) {
                                                                toast.error("Expiry date cannot be before Start date");
                                                                field.onChange("");
                                                                return;
                                                            }
                                                            field.onChange(d);
                                                        }}
                                                        placeholderText="YYYY-MM-DD"
                                                        minDate={new Date()}
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                    />
                                                    <FormFeedback className="d-block">{errors.expiry_date?.message as string}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={12}>
                                        <Label>Remarks</Label>
                                        <Controller
                                            name="remarks"
                                            control={control}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="textarea" {...field} value={field.value || ''} placeholder="Any remarks..." />
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label>License Front Image <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="image_front"
                                            control={control}
                                            rules={{ required: !existingFront && "Front Image is required" }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        onChange={(e) => onChange(e.target.files)}
                                                        accept="image/*"
                                                        invalid={!!errors.image_front}
                                                    />
                                                    {existingFront && !(value instanceof FileList && value.length > 0) && (
                                                        <a href={`${mediaUrl}${existingFront}`} target="_blank" rel="noreferrer" className="d-block mt-2 text-primary">View Current Document</a>
                                                    )}
                                                    {value instanceof FileList && value.length > 0 && (
                                                        <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2 text-primary">View New Document</a>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label>License Back Image <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="image_back"
                                            control={control}
                                            rules={{ required: !existingBack && "Back Image is required" }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        onChange={(e) => onChange(e.target.files)}
                                                        accept="image/*"
                                                        invalid={!!errors.image_back}
                                                    />
                                                    {existingBack && !(value instanceof FileList && value.length > 0) && (
                                                        <a href={`${mediaUrl}${existingBack}`} target="_blank" rel="noreferrer" className="d-block mt-2 text-primary">View Current Document</a>
                                                    )}
                                                    {value instanceof FileList && value.length > 0 && (
                                                        <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2 text-primary">View New Document</a>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>
                                </Row>

                                {/* Pending warning */}
                                {submissionBlocked && (
                                    <div className="alert alert-warning mt-3 py-2 text-danger">
                                        <strong>⚠ Blocked:</strong> {pendingMessage}
                                    </div>
                                )}

                                {hasPermission('license-renewal.edit') && (
                                    <div className="mt-4">
                                        <Btn color="primary" disabled={loading} type="submit">
                                            {loading ? <>Updating <Spinner size="sm" /></> : "Update"}
                                        </Btn>
                                    </div>
                                )}
                            </Form>
                        </CardBody>
                    </Card>
                </Container>
            )}
            <ToastContainer />
        </div>
    );
}
